// Fichier des immobilisations (/immobilisations/fichier) : liste + création.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { roleEffectif } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { SelecteurCompte } from "@/components/SelecteurCompte";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Building2, Plus, Loader2 } from "lucide-react";

const fmt = (n) => (n != null ? Math.round(n).toLocaleString("fr-FR") : "0");
const VIDE = {
  libelle: "", compte_immobilisation: "", compte_amortissement: "", compte_dotation: "",
  valeur_acquisition: "", valeur_residuelle: "0", methode: "lineaire", duree_annees: "",
  date_acquisition: "", date_mise_en_service: "", facture_origine: "",
};

export default function ImmobilisationsFichier() {
  const { user } = useAuth();
  const peutCreer = ["gerant", "administrateur", "comptable"].includes(roleEffectif(user));
  const peutCeder = ["gerant", "administrateur"].includes(roleEffectif(user));
  const [liste, setListe] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [ouvert, setOuvert] = useState(false);
  const [form, setForm] = useState(VIDE);
  const [taux, setTaux] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [cession, setCession] = useState(null); // {imm, prix, date}

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/immobilisations");
      setListe(data.immobilisations || []);
    } catch { setListe([]); }
    finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Taux calculés automatiquement dès que méthode + durée sont renseignées.
  useEffect(() => {
    if (form.duree_annees) {
      api.get("/immobilisations/taux", { params: { methode: form.methode, duree: parseInt(form.duree_annees, 10) } })
        .then(({ data }) => setTaux(data)).catch(() => setTaux(null));
    } else setTaux(null);
  }, [form.methode, form.duree_annees]);

  const creer = async () => {
    const obligatoires = ["libelle", "compte_immobilisation", "compte_amortissement", "compte_dotation",
      "valeur_acquisition", "duree_annees", "date_acquisition", "date_mise_en_service"];
    if (obligatoires.some((k) => !form[k])) { toast.error("Merci de renseigner tous les champs obligatoires."); return; }
    setEnvoi(true);
    try {
      await api.post("/immobilisations", {
        ...form,
        valeur_acquisition: parseFloat(form.valeur_acquisition),
        valeur_residuelle: parseFloat(form.valeur_residuelle || 0),
        duree_annees: parseInt(form.duree_annees, 10),
      });
      toast.success("Immobilisation créée.");
      setOuvert(false); setForm(VIDE); await charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "Création impossible.");
    } finally { setEnvoi(false); }
  };

  const ceder = async () => {
    if (!cession?.prix || !cession?.date) { toast.error("Prix et date de cession requis."); return; }
    try {
      const { data } = await api.post(`/immobilisations/${cession.imm.id}/ceder`, {
        prix_cession: parseFloat(cession.prix), date_cession: cession.date,
      });
      toast.success(`Cession enregistrée — ${data.type_resultat} de ${Math.abs(data.plus_value).toLocaleString("fr-FR")} XPF (OD en attente de validation).`);
      setCession(null);
      await charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "Cession impossible.");
    }
  };

  const Champ = ({ label, k, type = "text" }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={`immo-${k}`} type={type} value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
    </div>
  );

  return (
    <div className="fenua-fade-up" data-testid="page-immobilisations-fichier">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
            <Building2 className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <div>
            <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Immobilisations</h1>
            <p className="text-sm text-[#1D3D3D]/70">Fichier des immobilisations et plans d'amortissement.</p>
          </div>
        </div>
        {peutCreer && (
          <Dialog open={ouvert} onOpenChange={setOuvert}>
            <DialogTrigger asChild>
              <Button data-testid="bouton-nouvelle-immo" className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                <Plus className="h-4 w-4 mr-2" /> Nouvelle immobilisation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-immo">
              <DialogHeader><DialogTitle>Nouvelle immobilisation</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Champ label="Libellé *" k="libelle" /></div>
                <div>
                  <Label className="text-xs">Compte immobilisation (classe 2) *</Label>
                  <SelecteurCompte testId="immo-compte-immobilisation" onSelect={(c) => setForm((f) => ({ ...f, compte_immobilisation: c?.numero || "" }))} placeholder="Ex : 215000" />
                </div>
                <div>
                  <Label className="text-xs">Compte amortissement (classe 28) *</Label>
                  <SelecteurCompte testId="immo-compte-amortissement" onSelect={(c) => setForm((f) => ({ ...f, compte_amortissement: c?.numero || "" }))} placeholder="Ex : 281500" />
                </div>
                <div>
                  <Label className="text-xs">Compte dotation (classe 68) *</Label>
                  <SelecteurCompte testId="immo-compte-dotation" onSelect={(c) => setForm((f) => ({ ...f, compte_dotation: c?.numero || "" }))} placeholder="Ex : 681120" />
                </div>
                <Champ label="Valeur d'acquisition HT *" k="valeur_acquisition" type="number" />
                <Champ label="Valeur résiduelle" k="valeur_residuelle" type="number" />
                <div>
                  <Label className="text-xs">Méthode *</Label>
                  <Select value={form.methode} onValueChange={(v) => setForm({ ...form, methode: v })}>
                    <SelectTrigger data-testid="immo-methode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lineaire">Linéaire</SelectItem>
                      <SelectItem value="degressif">Dégressif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Champ label="Durée (années) *" k="duree_annees" type="number" />
                <Champ label="Date d'acquisition *" k="date_acquisition" type="date" />
                <Champ label="Date de mise en service *" k="date_mise_en_service" type="date" />
                <div className="col-span-2"><Champ label="Référence facture d'achat" k="facture_origine" /></div>
                {taux && (
                  <div data-testid="immo-taux-calcules" className="col-span-2 text-sm bg-[#E8F4F4]/60 rounded p-2 text-[#1D3D3D]">
                    Taux linéaire : <strong>{taux.taux_lineaire}%</strong>
                    {form.methode === "degressif" && <> · Taux dégressif : <strong>{taux.taux_degressif}%</strong> (coef {taux.coefficient})</>}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="confirmer-immo" onClick={creer} disabled={envoi} className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                  {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer l'immobilisation"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-[#4A8A8A]/20 rounded-lg overflow-x-auto">
        {chargement ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
        ) : (
          <table data-testid="table-immobilisations" className="w-full text-sm">
            <thead>
              <tr className="bg-[#E8F4F4] text-[#1D3D3D] text-left">
                <th className="px-3 py-2">N°</th><th className="px-3 py-2">Libellé</th>
                <th className="px-3 py-2">Acquisition</th><th className="px-3 py-2 text-right">Valeur</th>
                <th className="px-3 py-2 text-center">Méthode</th><th className="px-3 py-2 text-center">Durée</th>
                <th className="px-3 py-2 text-right">Cumul amort.</th><th className="px-3 py-2 text-right">VNC</th>
                <th className="px-3 py-2 text-center">Statut</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {liste.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-[#1D3D3D]/60">Aucune immobilisation.</td></tr>}
              {liste.map((i) => (
                <tr key={i.id} data-testid={`ligne-immo-${i.numero}`} className="border-b border-[#4A8A8A]/10">
                  <td className="px-3 py-2 font-mono text-[#4A8A8A]">{i.numero}</td>
                  <td className="px-3 py-2 font-medium text-[#1D3D3D]">{i.libelle}</td>
                  <td className="px-3 py-2 text-[#1D3D3D]/70">{i.date_acquisition}</td>
                  <td className="px-3 py-2 text-right">{fmt(i.valeur_acquisition)}</td>
                  <td className="px-3 py-2 text-center">{i.methode === "lineaire" ? "L" : "D"}</td>
                  <td className="px-3 py-2 text-center">{i.duree_annees} ans</td>
                  <td className="px-3 py-2 text-right">{fmt(i.cumul_amortissements)}</td>
                  <td className="px-3 py-2 text-right font-medium">{fmt(i.valeur_nette_comptable)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#4A8A8A]/15">{i.statut === "en_cours" ? "En cours" : i.statut === "cédé" ? "Cédé" : "Amorti"}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {peutCeder && i.statut !== "cédé" && (
                      <Button data-testid={`bouton-ceder-${i.numero}`} size="sm" variant="outline"
                        onClick={() => setCession({ imm: i, prix: "", date: new Date().toISOString().slice(0, 10) })}
                        className="text-[#1D3D3D] border-[#4A8A8A]/30">Céder</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialogue de cession */}
      <Dialog open={!!cession} onOpenChange={(o) => !o && setCession(null)}>
        <DialogContent data-testid="dialog-cession">
          <DialogHeader><DialogTitle>Céder {cession?.imm?.numero} — {cession?.imm?.libelle}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Prix de cession (XPF)</Label>
              <Input data-testid="cession-prix" type="number" value={cession?.prix || ""}
                onChange={(e) => setCession((c) => ({ ...c, prix: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Date de cession</Label>
              <Input data-testid="cession-date" type="date" value={cession?.date || ""}
                onChange={(e) => setCession((c) => ({ ...c, date: e.target.value }))} />
            </div>
            <p className="text-xs text-[#1D3D3D]/60">
              La plus ou moins-value sera calculée automatiquement et l'écriture OD de cession
              créée en attente de validation.
            </p>
          </div>
          <DialogFooter>
            <Button data-testid="confirmer-cession" onClick={ceder} className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
              Enregistrer la cession
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
